<template>
  <div class="container-fluid kb-page mt-4 mb-5">
    <header class="kb-head">
      <div>
        <h3 class="mb-1">Kortbetalinger</h3>
        <p class="kb-lead">
          Sjekk om en betaling gikk gjennom hos VIVA — på nett, i kassen og i kiosken. Søk på dag, klokkeslett og beløp.
        </p>
      </div>
      <span v-if="isSuperAdmin" class="kb-merchant" title="Følger merchanten du er logget inn på">
        <font-awesome-icon :icon="['fas', 'store']" /> {{ merchant }}
      </span>
    </header>

    <form class="kb-search" @submit.prevent="search()">
      <div class="kb-quick" role="group" aria-label="Hurtigvalg">
        <button type="button" class="kb-chip" @click="lastHour()">Siste time</button>
        <button type="button" class="kb-chip" @click="wholeDay(0)">I dag</button>
        <button type="button" class="kb-chip" @click="wholeDay(-1)">I går</button>
      </div>
      <div class="kb-fields">
        <div class="kb-field kb-field--day">
          <label class="kb-label" for="kb-day">Dag</label>
          <Datepicker id="kb-day" v-model="day" :locale="nbLocale" inputFormat="dd.MM.yyyy"
                      :upperLimit="today" :lowerLimit="earliestDay" class="form-control" />
        </div>
        <div class="kb-field">
          <label class="kb-label" for="kb-from">Fra kl.</label>
          <TimeSelect id="kb-from" v-model="timeFrom" :max="timeTo" aria-label="Fra kl." />
        </div>
        <div class="kb-field">
          <label class="kb-label" for="kb-to">Til kl.</label>
          <TimeSelect id="kb-to" v-model="timeTo" :min="timeFrom" aria-label="Til kl." />
        </div>
        <div class="kb-field">
          <label class="kb-label" for="kb-amount">Beløp (kr)</label>
          <input id="kb-amount" v-model="amount" type="text" inputmode="decimal" autocomplete="off"
                 placeholder="f.eks. 349" class="form-control" />
        </div>
        <div class="kb-actions">
          <button type="submit" class="bo_btn" :disabled="searching">{{ searching ? 'Søker…' : 'Søk' }}</button>
        </div>
      </div>
      <p class="kb-hint">Tomme klokkeslett = hele dagen. Beløpet finner både betalingen og en refusjon av samme beløp.
        VIVA viser de siste {{ historyDays }} dagene.</p>
    </form>

    <div v-if="error" class="kb-alert kb-alert--bad" role="alert">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" /> {{ error }}
      <span v-if="isSuperAdmin && result && result.configured === false">
        Fyll inn Data Services under <a href="/vivaPartnerConfig?tab=connection">VIVA Partner Config</a>.
      </span>
    </div>

    <div v-else-if="result && result.connected === false" class="kb-alert kb-alert--info">
      <font-awesome-icon :icon="['fas', 'info-circle']" />
      <template v-if="isSuperAdmin">{{ merchant }} har ingen VIVA-konto koblet til CAGISTA.</template>
      <template v-else>Du har ingen VIVA-konto koblet til ennå, så det er ingen kortbetalinger å vise her.</template>
    </div>

    <section v-else-if="result" class="kb-result" aria-live="polite">
      <div class="kb-summary">
        <div class="kb-summary__text">
          <strong>{{ countText }}</strong> {{ windowText }}<template v-if="searchedAmount"> på {{ kr(searchedAmount) }}</template>
        </div>
        <div v-if="rows.length" class="kb-summary__pills">
          <span v-if="counts.ok" class="kb-pill kb-pill--ok">{{ counts.ok }} gjennomført</span>
          <span v-if="counts.bad" class="kb-pill kb-pill--bad">{{ counts.bad }} avvist</span>
          <span v-if="counts.warn" class="kb-pill kb-pill--warn">{{ counts.warn }} venter</span>
          <span v-if="counts.muted" class="kb-pill kb-pill--muted">{{ counts.muted }} refundert / kansellert</span>
        </div>
      </div>

      <div v-if="result.truncated" class="kb-alert kb-alert--warn">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        Det er flere betalinger enn siden viser. Velg et kortere tidsrom eller skriv inn beløpet.
      </div>

      <div v-if="rows.length === 0" class="kb-empty">
        <p class="mb-2"><strong>VIVA har ingen betaling {{ emptyText }}.</strong></p>
        <ul class="mb-0">
          <li v-if="searchedAmount">Prøv uten beløp — kanskje kunden husker beløpet feil, eller det var tips med.</li>
          <li v-if="searchedTimes">Gjør tidsrommet større, eller trykk «I dag».</li>
          <li>Ble betalingen gjort nettopp? Søk igjen om et par minutter.</li>
        </ul>
      </div>

      <table v-else class="kb-table">
        <thead>
          <tr>
            <th scope="col">Tid</th>
            <th scope="col" class="kb-num">Beløp</th>
            <th scope="col">Status</th>
            <th scope="col">Type</th>
            <th scope="col">Hvor</th>
            <th scope="col">Kort</th>
            <th scope="col">Ordre</th>
            <th v-if="result.multiLocation" scope="col">Avdeling</th>
            <th scope="col"><span class="visually-hidden">Detaljer</span></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="t in rows" :key="t.id">
            <tr class="kb-row" :class="{ 'kb-row--open': open === t.id }" tabindex="0"
                :aria-expanded="open === t.id ? 'true' : 'false'"
                @click="toggle(t.id)" @keydown.enter.prevent="toggle(t.id)" @keydown.space.prevent="toggle(t.id)">
              <td data-label="Tid" class="kb-time"><span>{{ clock(t.time) }}<span v-if="!sameDay(t.time)" class="kb-day"> {{ shortDate(t.time) }}</span></span></td>
              <td data-label="Beløp" class="kb-num kb-amount" :class="{ 'kb-amount--neg': t.amount < 0 }">{{ kr(t.amount, t.currency) }}</td>
              <td data-label="Status">
                <div>
                  <span class="kb-pill" :class="'kb-pill--' + t.tone">{{ t.statusLabel }}</span>
                  <div v-if="t.reason" class="kb-reason">{{ t.reason }}</div>
                </div>
              </td>
              <td data-label="Type">{{ t.kindLabel }}</td>
              <td data-label="Hvor">{{ channelLabel(t.channel) }}</td>
              <td data-label="Kort" class="kb-card">{{ cardText(t) }}</td>
              <td data-label="Ordre" class="kb-ref">{{ t.reference || '–' }}</td>
              <td v-if="result.multiLocation" data-label="Avdeling">{{ t.location || '–' }}</td>
              <td class="kb-chevron" aria-hidden="true">
                <font-awesome-icon :icon="['fas', open === t.id ? 'chevron-up' : 'chevron-down']" />
              </td>
            </tr>
            <tr v-if="open === t.id" class="kb-detail">
              <td :colspan="result.multiLocation ? 9 : 8">
                <dl class="kb-dl">
                  <div><dt>Tidspunkt</dt><dd>{{ fullTime(t.time) }}</dd></div>
                  <div v-if="t.text"><dt>Tekst kunden så</dt><dd>{{ t.text }}</dd></div>
                  <div v-if="t.location"><dt>Avdeling</dt><dd>{{ t.location }}</dd></div>
                  <div><dt>Avregnet hos VIVA</dt><dd>{{ t.cleared ? 'Ja' : 'Ikke ennå' }}</dd></div>
                  <div><dt>Transaksjons-ID hos VIVA</dt><dd class="kb-mono">{{ t.id || '–' }}</dd></div>
                  <div v-if="t.orderCode"><dt>Ordrekode hos VIVA</dt><dd class="kb-mono">{{ t.orderCode }}</dd></div>
                  <div v-if="t.refundOf"><dt>Refusjon av</dt><dd class="kb-mono">{{ t.refundOf }}</dd></div>
                </dl>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </section>

    <details class="kb-help">
      <summary>Slik leser du statusen</summary>
      <dl class="kb-legend">
        <div><dt><span class="kb-pill kb-pill--ok">Gjennomført</span></dt><dd>Betalingen gikk gjennom, og pengene er trukket fra kortet.</dd></div>
        <div><dt><span class="kb-pill kb-pill--bad">Avvist</span></dt><dd>Betalingen gikk ikke gjennom, og ingen penger er trukket. Grunnen står under når VIVA oppgir den.</dd></div>
        <div><dt><span class="kb-pill kb-pill--warn">Pågår</span></dt><dd>VIVA venter fortsatt på svar. Den kan bli gjennomført eller avvist — søk igjen om litt.</dd></div>
        <div><dt><span class="kb-pill kb-pill--muted">Refundert</span></dt><dd>Betalingen gikk gjennom, men er senere betalt tilbake, helt eller delvis. Refusjonen står som en egen linje.</dd></div>
        <div><dt><span class="kb-pill kb-pill--muted">Kansellert</span></dt><dd>Betalingen ble annullert samme dag, og pengene trekkes ikke.</dd></div>
        <div><dt><span class="kb-pill kb-pill--warn">Innsigelse</span></dt><dd>Kunden har klaget på betalingen til banken sin.</dd></div>
      </dl>
    </details>
  </div>
</template>

<script>
import VivaReportService from "@/services/system/VivaReportService";
import UserUtil from "@/util/UserUtil";
import Datepicker from "@/components/other/ToggleDatepicker.vue";
import TimeSelect from "@/components/other/TimeSelect.vue";
import { nb } from "date-fns/locale";

const OSLO = "Europe/Oslo";
const HISTORY_DAYS = 240;

/** A calendar day from a Date, built from local parts: toISOString() would move it to UTC first. */
function dayKey(date) {
  const d = new Date(date);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

function hhmm(date) {
  return String(date.getHours()).padStart(2, "0") + ":" + String(date.getMinutes()).padStart(2, "0");
}

/**
 * «Kortbetalinger»: did a payment go through? The merchant's staff (EMPLOYEE or ADMIN), or CAGISTA support as
 * SUPER_ADMIN on that merchant, finds it here by day, time and amount.
 *
 * Everything shown comes from /api/payment/findVivaPayments, which keeps only this merchant's own VIVA transactions and
 * already speaks Norwegian (status, type, reason); the page formats and nothing more. It shows no raw VIVA data.
 */
export default {
  name: "VivaPaymentTransactions",
  components: { Datepicker, TimeSelect },
  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      today,
      earliestDay: new Date(today.getTime() - HISTORY_DAYS * 24 * 3600 * 1000),
      historyDays: HISTORY_DAYS,
      day: new Date(today),
      timeFrom: "",
      timeTo: "",
      amount: "",
      searching: false,
      error: "",
      result: null,
      searchedDay: "",
      searchedAmount: null,
      searchedTimes: false,
      open: ""
    };
  },
  computed: {
    nbLocale() { return nb; },
    isSuperAdmin() { return UserUtil.isSuperAdmin(); },
    merchant() {
      const user = this.$store.state.auth.user;
      return user && user.merchant ? user.merchant : "";
    },
    rows() { return (this.result && this.result.transactions) || []; },
    counts() {
      const c = { ok: 0, bad: 0, warn: 0, muted: 0 };
      this.rows.forEach((t) => { c[t.tone] = (c[t.tone] || 0) + 1; });
      return c;
    },
    countText() {
      const n = this.rows.length;
      return n === 0 ? "Ingen treff" : n === 1 ? "1 transaksjon" : n + " transaksjoner";
    },
    windowText() {
      if (!this.result) { return ""; }
      const from = new Date(this.result.from);
      const to = new Date(this.result.to);
      const day = new Intl.DateTimeFormat("nb-NO", { timeZone: OSLO, weekday: "long", day: "numeric", month: "long" }).format(from);
      if (!this.searchedTimes) { return day; }
      return day + " kl. " + this.clock(from.toISOString(), false) + "–" + this.clock(to.toISOString(), false);
    },
    emptyText() {
      let text = this.windowText;
      if (this.searchedAmount) { text += " på " + this.kr(this.searchedAmount); }
      return text;
    }
  },
  mounted() {
    this.search();
  },
  methods: {
    lastHour() {
      const now = new Date();
      const hourAgo = new Date(now.getTime() - 3600 * 1000);
      this.day = new Date(now);
      // Just after midnight the hour began yesterday; the window stays on today rather than span two days.
      this.timeFrom = dayKey(hourAgo) === dayKey(now) ? hhmm(hourAgo) : "00:00";
      this.timeTo = "";
      this.search();
    },
    wholeDay(offset) {
      const d = new Date(this.today);
      d.setDate(d.getDate() + offset);
      this.day = d;
      this.timeFrom = "";
      this.timeTo = "";
      this.search();
    },
    search() {
      if (!this.day) {
        this.error = "Velg en dag.";
        return;
      }
      const params = { merchant: this.merchant, date: dayKey(this.day) };
      if (this.timeFrom) { params.timeFrom = this.timeFrom; }
      if (this.timeTo) { params.timeTo = this.timeTo; }
      const amount = this.amount.trim();
      if (amount) { params.amount = amount; }

      this.searching = true;
      this.error = "";
      this.open = "";
      VivaReportService.findPayments(params).then(
        (response) => {
          const data = response.data || {};
          this.searching = false;
          this.result = data;
          this.searchedDay = params.date;
          this.searchedAmount = amount ? Number(amount.replace(/\s/g, "").replace(",", ".")) : null;
          this.searchedTimes = Boolean(params.timeFrom || params.timeTo);
          if (data.ok === false) {
            this.error = data.error || "Kunne ikke hente betalinger fra VIVA akkurat nå. Prøv igjen om litt.";
          }
        },
        (error) => {
          this.searching = false;
          const body = error && error.response ? error.response.data : null;
          this.error = (body && body.error) || "Kunne ikke hente betalinger fra VIVA akkurat nå. Prøv igjen om litt.";
        }
      );
    },
    toggle(id) {
      this.open = this.open === id ? "" : id;
    },
    /**
     * Where the payment was taken. The server reads it from the order number: P till, K kiosk, W web, Q a QR order at
     * the table. A payment without one falls back to VIVA's channel, which cannot tell a till from a kiosk.
     */
    channelLabel(channel) {
      return { TILL: "Kasse", KIOSK: "Kiosk", WEB: "Nett", QR: "Nett (QR)", TERMINAL: "Kasse/kiosk" }[channel] || "–";
    },
    /**
     * «Visa •••• 6686». The server names the card's type (Visa, Mastercard, BankAxept …) only when it is sure, so a
     * payment without one shows the four digits alone, as before.
     */
    cardText(t) {
      const text = [t.cardType, t.card ? "•••• " + t.card : ""].filter(Boolean).join(" ");
      return text || "–";
    },
    /** «kr 349,00», as VIVA Partner Config and Ordrer print it; a refund is «−kr 249,00». */
    kr(value, currency) {
      if (value === null || value === undefined || isNaN(Number(value))) { return "–"; }
      const n = Number(value);
      const digits = Math.abs(n).toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const sign = n < 0 ? "−" : "";
      return currency && currency !== "NOK" ? sign + digits + " " + currency : sign + "kr " + digits;
    },
    clock(iso, seconds = true) {
      if (!iso) { return "–"; }
      const options = { timeZone: OSLO, hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
      if (seconds) { options.second = "2-digit"; }
      return new Intl.DateTimeFormat("nb-NO", options).format(new Date(iso));
    },
    sameDay(iso) {
      if (!iso || !this.searchedDay) { return true; }
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone: OSLO, year: "numeric", month: "2-digit", day: "2-digit" })
        .format(new Date(iso));
      return parts === this.searchedDay;
    },
    shortDate(iso) {
      return new Intl.DateTimeFormat("nb-NO", { timeZone: OSLO, day: "numeric", month: "short" }).format(new Date(iso));
    },
    fullTime(iso) {
      if (!iso) { return "–"; }
      return new Intl.DateTimeFormat("nb-NO", {
        timeZone: OSLO, weekday: "long", day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23"
      }).format(new Date(iso));
    }
  }
};
</script>

<style scoped>
.kb-page { max-width: 1400px; margin: 0 auto; }

.kb-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.kb-lead { color: var(--bo-muted); margin: 0; }
.kb-merchant {
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px;
  background: var(--bo-brand-tint); color: var(--bo-brand-ink); font-weight: 600; font-size: 0.85rem;
  text-transform: uppercase; letter-spacing: 0.02em;
}

.kb-search {
  margin-top: 16px; padding: 16px; border: 1px solid var(--bo-line); border-radius: 10px; background: var(--bo-surface);
}
.kb-quick { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.kb-chip {
  border: 1px solid var(--bo-line); background: var(--bo-surface-2); color: var(--bo-ink); border-radius: 999px;
  padding: 4px 14px; font-size: 0.9rem; min-height: 36px; transition: all .2s ease;
}
.kb-chip:hover { border-color: #ECB109; color: var(--bo-brand-ink); }
.kb-fields {
  display: grid; gap: 12px; align-items: end;
  grid-template-columns: minmax(150px, 1.2fr) minmax(110px, 0.8fr) minmax(110px, 0.8fr) minmax(120px, 1fr) auto;
}
.kb-label { display: block; font-size: 0.85rem; color: var(--bo-muted); margin-bottom: 4px; }
.kb-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.kb-actions .bo_btn { min-height: 38px; }
.kb-hint { color: var(--bo-muted); font-size: 0.82rem; margin: 10px 0 0; }

.kb-alert { margin-top: 16px; padding: 10px 14px; border-radius: 8px; border: 1px solid; }
.kb-alert--bad { background: var(--bo-bad-bg); color: var(--bo-bad-fg); border-color: var(--bo-bad-line); }
.kb-alert--info { background: var(--bo-info-bg); color: var(--bo-info-fg); border-color: var(--bo-info-line); }
.kb-alert--warn { background: var(--bo-warn-bg); color: var(--bo-warn-fg); border-color: var(--bo-warn-line); }
.kb-alert a { color: inherit; text-decoration: underline; }

.kb-result { margin-top: 20px; }
.kb-summary { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.kb-summary__text { color: var(--bo-ink-2); }
.kb-summary__text strong { color: var(--bo-ink); }
.kb-summary__pills { display: flex; gap: 6px; flex-wrap: wrap; }

.kb-pill {
  display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.82rem; font-weight: 600;
  border: 1px solid transparent; white-space: nowrap;
}
.kb-pill--ok { background: var(--bo-ok-bg); color: var(--bo-ok-fg); border-color: var(--bo-ok-line); }
.kb-pill--bad { background: var(--bo-bad-bg); color: var(--bo-bad-fg); border-color: var(--bo-bad-line); }
.kb-pill--warn { background: var(--bo-warn-bg); color: var(--bo-warn-fg); border-color: var(--bo-warn-line); }
.kb-pill--muted { background: var(--bo-surface-2); color: var(--bo-muted); border-color: var(--bo-line); }

.kb-empty {
  padding: 16px; border: 1px dashed var(--bo-line); border-radius: 10px; color: var(--bo-ink-2); background: var(--bo-surface);
}

.kb-table { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--bo-surface);
  border: 1px solid var(--bo-line); border-radius: 10px; overflow: hidden; }
.kb-table th { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bo-muted);
  font-weight: 600; padding: 10px 12px; border-bottom: 1px solid var(--bo-line); background: var(--bo-surface-2); }
.kb-table td { padding: 10px 12px; border-bottom: 1px solid var(--bo-line); vertical-align: top; color: var(--bo-ink); }
.kb-row { cursor: pointer; }
.kb-row:hover td, .kb-row--open td { background: var(--bo-surface-2); }
.kb-row:focus-visible { outline: 2px solid #ECB109; outline-offset: -2px; }
.kb-num { text-align: right; }
.kb-time { font-variant-numeric: tabular-nums; white-space: nowrap; font-weight: 600; }
.kb-day { color: var(--bo-muted); font-weight: 400; font-size: 0.85rem; }
.kb-amount { font-variant-numeric: tabular-nums; white-space: nowrap; font-weight: 600; }
.kb-amount--neg { color: var(--bo-muted); }
.kb-reason { color: var(--bo-alert-ink); font-size: 0.85rem; margin-top: 4px; }
.kb-card { white-space: nowrap; }
.kb-ref { overflow-wrap: anywhere; }
.kb-chevron { color: var(--bo-muted); width: 32px; text-align: center; }
.kb-detail td { background: var(--bo-surface-2); }
.kb-dl { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr)); gap: 10px 20px; margin: 0; }
.kb-dl dt, .kb-legend dt { font-size: 0.8rem; color: var(--bo-muted); font-weight: 600; }
.kb-dl dd { margin: 0; color: var(--bo-ink); overflow-wrap: anywhere; }
.kb-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.85rem; word-break: break-all; }

.kb-help { margin-top: 24px; color: var(--bo-ink-2); }
.kb-help summary { cursor: pointer; color: var(--bo-muted); }
.kb-legend { margin: 12px 0 0; display: grid; gap: 8px; max-width: 820px; }
.kb-legend > div { display: grid; grid-template-columns: 190px 1fr; gap: 12px; align-items: baseline; }
.kb-legend dd { margin: 0; }

/* A finger (touch.css): the quick choices reach the 48px floor and lose the hover tint Android leaves behind. */
@media (pointer: coarse) {
  .kb-chip { min-height: var(--bo-touch-target, 48px); padding: 8px 18px; }
  .kb-chip:hover { border-color: var(--bo-line); color: var(--bo-ink); }
  .kb-chip:active { background: var(--bo-brand-tint); border-color: #ECB109; }
  .kb-row td { padding-top: 14px; padding-bottom: 14px; }
}

@media (max-width: 991px) {
  .kb-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .kb-field--day { grid-column: 1 / -1; }
  .kb-actions { grid-column: 1 / -1; }
}

/* A phone: each payment is a card, the column name beside its value. */
@media (max-width: 767px) {
  .kb-table, .kb-table tbody, .kb-table tr, .kb-table td { display: block; width: 100%; }
  .kb-table { border: 0; background: transparent; }
  .kb-table thead { display: none; }
  .kb-row { border: 1px solid var(--bo-line); border-radius: 10px; margin-bottom: 10px; background: var(--bo-surface); overflow: hidden; }
  .kb-row--open { border-bottom-left-radius: 0; border-bottom-right-radius: 0; margin-bottom: 0; }
  /* Each cell holds ONE element besides its label, so the grid has exactly two columns to fill. */
  .kb-table td { border-bottom: 0; padding: 6px 12px; display: grid; grid-template-columns: 90px 1fr; gap: 8px;
    align-items: baseline; justify-items: start; text-align: left; }
  .kb-table td::before { content: attr(data-label); color: var(--bo-muted); font-size: 0.8rem; font-weight: 600; }
  .kb-table td.kb-chevron { display: none; }
  .kb-detail { border: 1px solid var(--bo-line); border-top: 0; border-radius: 0 0 10px 10px; margin-bottom: 10px; overflow: hidden; }
  .kb-detail td { display: block; }
  .kb-detail td::before { content: none; }
  .kb-legend > div { grid-template-columns: 1fr; gap: 4px; }
}
</style>
